import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { TourOverlay } from './TourOverlay'
import { ownerTour, staffTour, clientTour, type TourStep } from './tours'

type TourType = 'owner' | 'staff' | 'client'

interface TourContextValue {
  isActive: boolean
  currentStep: number
  totalSteps: number
  tourType: TourType | null
  startTour: (type: TourType) => void
  endTour: () => void
  nextStep: () => void
  prevStep: () => void
  skipTour: () => void
  hasSeenTour: (type: TourType) => boolean
}

const TourContext = createContext<TourContextValue | null>(null)

const noopTour: TourContextValue = {
  isActive: false,
  currentStep: 0,
  totalSteps: 0,
  tourType: null,
  startTour: () => {},
  endTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  skipTour: () => {},
  hasSeenTour: () => false,
}

export function useTour() {
  const ctx = useContext(TourContext)
  return ctx ?? noopTour
}

const STORAGE_KEY = 'jewelry-studio-tours-seen'

function getSeenTours(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function markTourSeen(type: TourType) {
  const seen = getSeenTours()
  seen[type] = true
  localStorage.setItem(STORAGE_KEY, JSON.stringify(seen))
}

function getTourSteps(type: TourType): TourStep[] {
  switch (type) {
    case 'owner': return ownerTour
    case 'staff': return staffTour
    case 'client': return clientTour
  }
}

export function TourProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false)
  const [tourType, setTourType] = useState<TourType | null>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [steps, setSteps] = useState<TourStep[]>([])
  const navigate = useNavigate()
  const location = useLocation()

  // Navigate to the step's required page if needed
  const navigateToStep = useCallback((step: TourStep) => {
    if (step.route && location.pathname !== step.route) {
      navigate(step.route)
    }
  }, [location.pathname, navigate])

  const startTour = useCallback((type: TourType) => {
    const tourSteps = getTourSteps(type)
    setTourType(type)
    setSteps(tourSteps)
    setCurrentStep(0)
    setIsActive(true)
    if (tourSteps[0]?.route) {
      navigate(tourSteps[0].route)
    }
  }, [navigate])

  const endTour = useCallback(() => {
    if (tourType) markTourSeen(tourType)
    setIsActive(false)
    setTourType(null)
    setCurrentStep(0)
    setSteps([])
  }, [tourType])

  const skipTour = useCallback(() => {
    if (tourType) markTourSeen(tourType)
    setIsActive(false)
    setTourType(null)
    setCurrentStep(0)
    setSteps([])
  }, [tourType])

  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      const next = currentStep + 1
      setCurrentStep(next)
      navigateToStep(steps[next])
    } else {
      endTour()
    }
  }, [currentStep, steps, navigateToStep, endTour])

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const prev = currentStep - 1
      setCurrentStep(prev)
      navigateToStep(steps[prev])
    }
  }, [currentStep, steps, navigateToStep])

  const hasSeenTour = useCallback((type: TourType) => {
    return !!getSeenTours()[type]
  }, [])

  // Wait for navigation to complete before showing overlay
  const [ready, setReady] = useState(false)
  useEffect(() => {
    if (!isActive) { setReady(false); return }
    const timer = setTimeout(() => setReady(true), 350)
    return () => clearTimeout(timer)
  }, [isActive, currentStep])

  return (
    <TourContext.Provider value={{
      isActive, currentStep, totalSteps: steps.length,
      tourType, startTour, endTour, nextStep, prevStep, skipTour, hasSeenTour
    }}>
      {children}
      {isActive && ready && steps[currentStep] && (
        <TourOverlay
          step={steps[currentStep]}
          stepIndex={currentStep}
          totalSteps={steps.length}
          onNext={nextStep}
          onPrev={prevStep}
          onSkip={skipTour}
        />
      )}
    </TourContext.Provider>
  )
}
